import { lazy, useEffect, useMemo, useRef, useState } from "react";
import { useSnackbar } from "notistack";
import { Box, Button, Stack, Step, StepLabel, Stepper, useTheme, StepConnector, IconButton, Divider, Typography, alpha } from "@mui/material";
import { FormProvider, useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { useGetWorkspaceSetupQuery, usePutSetupWorkspaceMutation } from "src/context/api/workspaces/api";
import { useParams, useRouter } from "src/routes/hooks";
import { WorkspaceFormData, StepKey, validationSchema, emptyValues } from "./setup/types";
import { SplashScreen } from "src/components/loading-screen";
import { useBoolean } from "src/hooks/use-boolean";
import Scrollbar from "src/components/scrollbar";
import { LoadingButton } from "@mui/lab";
import Iconify from "src/components/iconify";

import SetupContext from "./setup/context";
import useCountries from "src/hooks/use-countries";
import useJobs from "src/hooks/use-jobs";

const InformationSection = lazy(() => import("./setup/informations"));
const SettingsSections = lazy(() => import("./setup/settings-sections"));
const ScrappingSection = lazy(() => import("./setup/scrapping"));

function WorkspaceSetup() {
    const { id } = useParams()
    const [error, setError] = useState(null)
    const buttonRef = useRef<HTMLDivElement>(null)
    const containerRef = useRef<HTMLDivElement>(null)
    const stepContainerRef = useRef<HTMLDivElement>(null)
    const stepConnectorRef = useRef<HTMLSpanElement>(null)
    const validation = useBoolean()
    const titleRef = useRef<HTMLDivElement>(null)
    const [, setStepConnectorHeight] = useState(0)
    const { value: scrolling, onFalse: stopScrolling } = useBoolean()
    const theme = useTheme()
    const steps: StepKey[] = ['information', 'settings', 'scraping'];
    const [step, setStep] = useState<StepKey>('information')
    const { data: workspace, isFetching } = useGetWorkspaceSetupQuery({ id: parseInt(id as any) }, {
        skip: !id,
    })
    const [putSetupWorkspace] = usePutSetupWorkspaceMutation()
    const { enqueueSnackbar } = useSnackbar()
    const [ready, setReady] = useState(false);
    const { isFetching: isFetchingCountries, countries } = useCountries();

    useJobs();

    const defaultValues = useMemo(() => {
        if (workspace) {
            return {
                ready: true,
                general: {
                    name: workspace.name || "",
                    url: workspace.url || "",
                    friendlyDomains: workspace.friendlyDomains || [],
                    template: workspace.template || "",
                    averageCart: workspace.averageCart || 0,
                    currency: workspace.currency || "usd",
                    custom: workspace.custom || false,
                    bqTable: workspace.bqTable || "",
                    shoppingIncluded: workspace.shoppingIncluded || false,
                    platforms: ["Google", "Bing"],
                },
                display: {
                    showSavings: workspace.showSavings ?? true,
                    brandInfringement: (workspace.showPlaAlert && workspace.showTxtAlert) || false,
                },
                integrations: workspace.integrations || [],
                dataConfig: {
                    // NON BRAND
                    nonbrand: workspace.nonbrand || false,
                    nonbrand_include_pmax_campaigns: workspace.nonbrand_include_pmax_campaigns || false,
                    nonbrand_visibility: workspace.nonbrand_visibility || ["brand specialist", "admin", "consultant"],

                    // BRAND PULSE 
                    brandpulse: workspace.brandpulse || false,
                    brandpulse_visibility: workspace.brandpulse_visibility || ["brand specialist", "admin", "consultant"],

                    // BRAND PULSE 
                    conquestmode: workspace.conquestmode || false,
                    conquestmode_visibility: workspace.conquestmode_visibility || ["brand specialist", "admin", "consultant"],

                    // ORGANIC CONVERSIONS
                    organic_conversion_mode: workspace.organic_conversion_mode ?? 'total',
                    organic_factor: workspace.organic_factor ?? null,
                    // LOCAL
                    local: workspace.local || false,
                    local_visibility: workspace.local_visibility || ["brand specialist", "admin", "consultant"],
                    geotargets: workspace.geotargets || [],

                    selected_countries: workspace.selected_countries || [],
                    branded_keywords: workspace.branded_keywords || [],
                    excluded_keywords: workspace.excluded_keywords || [],
                    exclude_for_non_branded: workspace.exclude_for_non_branded || [],
                    country_region_mapping: workspace.country_region_mapping || [],
                    mapping_type: 'campaign_names' as const, // Default to campaign_names since API shows campaign_region_mapping
                    google_ads: {
                        nonbrand_pmax_campaigns: workspace.google_ads?.nonbrand_pmax_campaigns || [],
                        use_region_mapping: (workspace.country_region_mapping || []).length > 0 || false,
                        branded_search_campaigns: workspace.google_ads?.branded_search_campaigns || [],
                        competitor_search_campaigns: workspace.google_ads?.competitor_search_campaigns || [],
                        branded_shopping_campaigns: workspace.google_ads?.branded_shopping_campaigns || [],
                        branded_pmax_campaigns: workspace.google_ads?.branded_pmax_campaigns || [],
                        account_region_mapping: workspace.google_ads?.account_region_mapping || [],
                        campaign_region_mapping: workspace.google_ads?.campaign_region_mapping || [],
                        conversion_actions: workspace.google_ads?.conversion_actions || [],
                        exclude_non_branded: workspace.google_ads?.exclude_non_branded || false,
                        customer_id: workspace.google_ads?.customer_id || [],
                    },
                    google_analytics: {
                        property_id: workspace.google_analytics?.property_id || [],
                        conversion_events: workspace.google_analytics?.conversion_events || [],
                    },
                    google_search_console: {
                        site_url: workspace.google_search_console?.site_url || [],
                        country_site_url_mapping: workspace.google_search_console?.country_site_url_mapping || [],
                    },
                },
                scraping: workspace.scrapping || [],
                savings: workspace.savings?.map(s => ({
                    platform: s.platform,
                    region_name: s.region_name,
                    blended_cpc_ref: s.blended_cpc_ref,
                })) || [],
            };
        }
    }, [workspace]);

    const methods = useForm<WorkspaceFormData>({
        resolver: yupResolver(validationSchema) as any,
        defaultValues: defaultValues ?? emptyValues,
    })

    useEffect(() => {
        methods.reset(defaultValues ?? emptyValues)
    }, [defaultValues, methods])


    const {
        handleSubmit,
    } = methods

    const onSubmit = (data: WorkspaceFormData): void => {
        const formData = Object.assign({ id: parseInt(id as any) }, data)

        putSetupWorkspace(formData)
            .unwrap()
            .then((res) => {
                enqueueSnackbar("Workspace setup updated successfully", { variant: "success" })
            })
            .catch((err) => {
                enqueueSnackbar("Failed to update workspace setup", { variant: "error" })
            })
    }

    const getProp = function (element: any, prop: string): number {
        if (!element) return 0;
        return getProp(element.offsetParent, prop) + element[prop];
    };

    useEffect(() => {
        if (!stepContainerRef.current || scrolling) return;
        // Calculer une hauteur plus raisonnable pour le connecteur
        // Au lieu d'utiliser window.innerHeight, on utilise une hauteur fixe ou calculée différemment
        const containerHeight = stepContainerRef.current.offsetHeight;
        const stepHeight = 60; // Hauteur approximative d'un step
        const remainingSteps = steps.length - steps.indexOf(step) - 1;
        const calculatedHeight = Math.min(remainingSteps * stepHeight, containerHeight * 0.6);
        setStepConnectorHeight(calculatedHeight);
    }, [stepContainerRef.current, step, steps]);

    // Ajouter un useEffect pour gérer le resize de la fenêtre
    useEffect(() => {
        const handleResize = () => {
            // Recalculer la hauteur du connecteur quand la fenêtre change de taille
            if (stepContainerRef.current && !scrolling) {
                const containerHeight = stepContainerRef.current.offsetHeight;
                const stepHeight = 60;
                const remainingSteps = steps.length - steps.indexOf(step) - 1;
                const calculatedHeight = Math.min(remainingSteps * stepHeight, containerHeight * 0.6);
                setStepConnectorHeight(calculatedHeight);
            }
        };

        window.addEventListener('resize', handleResize);
        window.addEventListener("scrollend", stopScrolling)
        return () => {
            window.removeEventListener('resize', handleResize);
            window.removeEventListener("scrollend", stopScrolling)
        }
    }, [step, scrolling, steps]);

    const router = useRouter()

    if (isFetching || isFetchingCountries) return <SplashScreen />

    const renderStepConnector = <StepConnector ref={stepConnectorRef} sx={{
        ".MuiStepConnector-line": {
            borderLeftColor: alpha(theme.palette.primary.main, 0),
            height: "100%"
        }
    }} />


    return (
        <SetupContext.Provider value={{
            workspace,
            countries: countries?.map((country) => ({ name: country.country_name, code: country.country_code })) as { name: string, code: string }[],
            isFetchingCountries,
            error,
            formMethods: methods,
            ready: (methods.formState.defaultValues as any)?.ready ?? false,
            setReady,
            setError,
            validation: validation.value,
            onInvalid: validation.onFalse,
            onValid: validation.onTrue,
        }}>
            <Stack className="workspace-setup" height="calc(100vh - 104px)" direction="row" marginTop={-4} alignItems="flex-start" spacing={3} px={"24px"}>
                <Box height="100%" minWidth={240} paddingRight={2} paddingTop={3} sx={{ alignSelf: 'flex-start', paddingBottom: 2 }}>
                    <Box ref={titleRef} display="flex" alignItems="center" justifyContent="space-between">
                        <Typography mb={2} variant="h5"> <IconButton onClick={() => router.push('/admin/cockpit')}><Iconify fontSize={36} icon="eva:arrow-ios-back-fill" /></IconButton> Workspace Setup</Typography>
                    </Box>
                    <Stepper sx={{
                        height: `calc(100% - ${buttonRef.current?.clientHeight}px - ${titleRef.current?.clientHeight}px)`,
                        overflow: 'auto',
                        position: "relative",
                        '& .MuiStep-root': {
                            minHeight: '48px',
                        }
                    }} connector={null} orientation="vertical">
                        <Step index={0} active={step === 'information'} onClick={() => setStep('information')} sx={{ cursor: 'pointer' }}>
                            <StepLabel>Workspace information</StepLabel>
                        </Step>
                        {step === "information" && renderStepConnector}
                        <Step index={1} active={step === 'settings'} onClick={() => setStep('settings')} sx={{ cursor: 'pointer' }}>
                            <StepLabel>Settings</StepLabel>
                        </Step>
                        {step === "settings" && renderStepConnector}
                        <Step index={2} active={step === 'scraping'} onClick={() => setStep('scraping')} sx={{ cursor: 'pointer' }}>
                            <StepLabel>Scraping</StepLabel>
                        </Step>
                        {step === "scraping" && renderStepConnector}
                    </Stepper>
                    <Stack ref={buttonRef} direction="column" justifyContent="space-between" spacing={2}>
                        {!!error && (
                            <Typography variant="subtitle2" color="error">{error}</Typography>
                        )}
                        <LoadingButton loading={!validation.value} disabled={!!error} size="small" type="submit" onClick={() => {
                            onSubmit(methods.getValues());
                        }} variant="contained" color="primary">
                            Save Workspace
                        </LoadingButton>
                        <Button
                            size="small"
                            onClick={() => {
                                const idx = steps.indexOf(step);
                                if (idx < steps.length - 1) setStep(steps[idx + 1]);
                            }}
                            variant="outlined"
                            color="primary"
                        >
                            Next
                        </Button>
                        <Button
                            size="small"
                            onClick={() => {
                                const idx = steps.indexOf(step);
                                if (idx > 0) setStep(steps[idx - 1]);
                            }}
                            variant="outlined"
                            color="primary"
                        >
                            Back
                        </Button>
                    </Stack>
                </Box>
                <Divider orientation="vertical" flexItem />
                <Scrollbar sx={{
                    height: containerRef.current?.clientHeight,
                    maxHeight: 'calc(100vh - 104px)',
                    paddingTop: 3,
                    paddingRight: 2,
                    overflow: 'auto',
                }}>
                    <Box sx={{ paddingX: 2 }} flexGrow={1} overflow="hidden">
                        <Stack direction="column" spacing={2}>
                            <FormProvider {...methods}>
                                <form onSubmit={handleSubmit(onSubmit)}>
                                    {step === 'information' && <InformationSection />}
                                    <SettingsSections step={step} />
                                    {step === 'scraping' && <ScrappingSection />}
                                </form>
                            </FormProvider>
                        </Stack>
                    </Box>
                </Scrollbar>
            </Stack>
        </SetupContext.Provider>
    );
}
export default WorkspaceSetup;